import React, { memo, useMemo } from 'react';
import Avatar from 'react-avatar';
import { formatTimeFromDate } from '../../utils/utility';
import DoubleChecked from '../../assets/images/DoubleChecked';
import { MESSAGE_STATUS, MSG_USER_ROLE } from '../../utils/messageHelpers';
import moment from 'moment';

function getMessageStatusIcon(status) {
  switch (status) {
    case MESSAGE_STATUS.SENDING:
      return <i className="fa-solid fa-clock text-secondary" title="Sending..."></i>;
    case MESSAGE_STATUS.SENT:
      return <i className="fa-solid fa-check text-secondary" title="Sent"></i>;
    case MESSAGE_STATUS.FAILED:
      return <i className="fa-solid fa-circle-xmark text-danger" title="Failed"></i>;
    case MESSAGE_STATUS.DELIVERED:
      return <DoubleChecked color="#6c757d" />;
    case MESSAGE_STATUS.READ:
      return <DoubleChecked color="#2457A8" />;
    default:
      return null;
  }
}

// Memoized date label function to avoid recalculation
const getDateLabel = (messageDate) => {
  const today = moment().startOf('day');
  const yesterday = moment().subtract(1, 'day').startOf('day');
  const messageDay = moment(messageDate).startOf('day');

  if (messageDay.isSame(today)) {
    return 'Today';
  } else if (messageDay.isSame(yesterday)) {
    return 'Yesterday';
  } else {
    // For older dates, show day name or full date
    const daysDiff = today.diff(messageDay, 'days');
    if (daysDiff <= 7) {
      return messageDay.format('dddd'); // Monday, Tuesday, etc.
    } else {
      return messageDay.format('MMMM D, YYYY'); // January 15, 2024
    }
  }
};
const MemoizedAvatar = memo(({ name, src, size = 50 }) => {
  return (
    <Avatar
      name={name}
      src={src}
      size={size}
      className="rounded-pill object-fit-cover border-white border border-2"
      maxInitials={2}
    />
  );
});

// Date divider component
const DateDivider = memo(({ dateLabel, hasLine = false, id }) => {
  return (
    <div className="col-12 my-3" data-newmsg-id={id}>
      <div className="d-flex align-items-center justify-content-center w-75 mx-auto">
        {hasLine && (
          <div className="flex-grow-1 ms-auto">
            <hr className="my-0 border-color-logo op-1" />
          </div>
        )}
        <div className="mx-3">
          <span
            className="badge bg-light text-dark px-3 py-2 fs-12 fw-400"
            style={{ borderRadius: '15px' }}>
            {dateLabel}
          </span>
        </div>
        {hasLine && (
          <div className="flex-grow-1">
            <hr className="my-0 border-color-logo op-1" />
          </div>
        )}
      </div>
    </div>
  );
});

DateDivider.displayName = 'DateDivider';

const MessageItem = memo(
  ({ message, index, isEmployer, headerData, activeChatInfo, formattedDateTime }) => {
    if (isEmployer) {
      return (
        <div
          className="col-xl-10 col-lg-12 ms-auto my-3"
          key={`employer-${message.id || index}`}
          data-message-id={`message-${message.id}`}>
          <div className="p-0 m-0 d-flex justify-content-end">
            <div className="me-3" style={{ maxWidth: '92%' }}>
              <div
                className="p-0 ms-auto mb-2 text-white p-2 bg-secondary lh-base px-4 fw-500 bg-opacity-50 rounded-top rounded-start fs-14 tiptap"
                style={{
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  width: 'fit-content',
                  minWidth: '80px'
                }}>
                <span dangerouslySetInnerHTML={{ __html: message.content }}></span>
              </div>
              <p className="mb-0 lh-base fs-12 text-secondary text-end">
                <span className="me-2">{formattedDateTime}</span>
                <span className="me-2">{getMessageStatusIcon(message.status)}</span>
              </p>
            </div>
            <div className="jd-comment-pic">
              <MemoizedAvatar
                name={headerData?.name}
                src={`${headerData?.image_url}${headerData?.employer_image}`}
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className="col-xl-10 col-lg-12 me-auto my-3"
        key={`candidate-${message.id || index}`}
        data-message-id={`message-${message.id}`}>
        <div className="p-0 m-0 d-flex flex-wrap">
          <div className="jd-comment-pic">
            <MemoizedAvatar
              name={`${activeChatInfo.first_name} ${activeChatInfo.last_name}`}
              src={`${activeChatInfo.imageurl}${activeChatInfo.image}`}
            />
          </div>
          <div className="ms-3" style={{ maxWidth: '92%' }}>
            <div className="p-0 m-0 mb-2 p-2 bg-white lh-base px-4 fw-400 rounded-top rounded-end fs-14 tiptap">
              <span dangerouslySetInnerHTML={{ __html: message.content }}></span>
            </div>
            <p className="mb-0 lh-base fs-12 text-secondary">{formattedDateTime}</p>
          </div>
        </div>
      </div>
    );
  }
);

MessageItem.displayName = 'MessageItem';

// Optimized grouping function that pre-calculates date labels and formatted times
const groupMessagesByDate = (messages, newUnreadMessageId) => {
  if (!messages || messages.length === 0) return [];

  const grouped = [];
  let currentDate = null;

  messages.forEach((message, index) => {
    const messageDate = moment(message.createdAt).format('YYYY-MM-DD');

    const dateLabel = getDateLabel(message.createdAt);
    if (
      newUnreadMessageId &&
      index !== 0 &&
      message?.id?.toString() === newUnreadMessageId?.toString()
    ) {
      grouped.push({
        type: 'LAST_READ',
        id: `divider-${newUnreadMessageId}`
      });
    }

    // Add date divider if date changed
    if (messageDate !== currentDate) {
      grouped.push({
        type: 'divider',
        dateLabel,
        id: `divider-${messageDate}`
      });
      currentDate = messageDate;
    }

    let formattedDateTime;
    if (dateLabel === 'Today') {
      formattedDateTime = formatTimeFromDate(message.createdAt);
    } else {
      formattedDateTime = `${dateLabel} ${formatTimeFromDate(message.createdAt)}`;
    }

    grouped.push({
      type: 'message',
      ...message,
      originalIndex: index,
      dateLabel,
      formattedDateTime
    });
  });

  return grouped;
};

const MessagesList = ({
  chatState,
  headerData,
  messagesScrollRef,
  chatEndRef,
  onScrollToTop,
  isMini = false
}) => {
  // Memoize the grouped items with proper dependency
  const groupedItems = useMemo(() => {
    return groupMessagesByDate(chatState.currentMessages, chatState.firstUnreadMessageId);
  }, [chatState.currentMessages, chatState.firstUnreadMessageId]);
  // Memoize the rendered items to prevent unnecessary re-renders
  const renderedItems = useMemo(() => {
    return groupedItems.map((item) => {
      if (item.type === 'LAST_READ') {
        return <DateDivider key={item.id} id={item.id} dateLabel={'Read'} hasLine />;
      }
      if (item.type === 'divider') {
        return <DateDivider key={item.id} id={item.id} dateLabel={item.dateLabel} />;
      } else {
        return (
          <MessageItem
            key={item.id || `msg-${item.originalIndex}`}
            message={item}
            index={item.originalIndex}
            isEmployer={item.senderType === MSG_USER_ROLE.EMPLOYER}
            headerData={headerData}
            formattedDateTime={item.formattedDateTime}
            activeChatInfo={chatState.activeChatInfo}
          />
        );
      }
    });
  }, [groupedItems, headerData, chatState.activeChatInfo]);

  return (
    <main
      className="main-content"
      style={{
        height: isMini ? '310px' : '400px',
        overflowY: 'auto',
        scrollBehavior: 'auto',
        overflowAnchor: 'none'
      }}
      onScroll={onScrollToTop}
      ref={messagesScrollRef}>
      {chatState.isLoading && (
        <div
          className="text-center py-2"
          style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'transparent' }}>
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">Loading more...</span>
          </div>
        </div>
      )}

      <div className="container-fluid">
        <div className="row" ref={chatEndRef}>
          {renderedItems}
        </div>
      </div>
    </main>
  );
};

export default memo(MessagesList);
